import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_CONFIG, truncateForMeta, absoluteUrl } from '@/config/seo';

export interface PageMetaProps {
  /** Page title - will be appended with " | FullBook" if not already present */
  title: string;
  /** Meta description (max 155 chars recommended) */
  description: string;
  /** Canonical path (e.g. "/faq") - will be converted to absolute URL */
  canonical?: string;
  /** Override OG image URL (absolute). Leave empty to use default from index.html */
  ogImage?: string;
  /** Set noindex for pages that should not be indexed (auth, admin, etc.) */
  noIndex?: boolean;
  /** Open Graph type - default "website" */
  ogType?: 'website' | 'article';
}

export function PageMeta({
  title,
  description,
  canonical,
  ogImage,
  noIndex = false,
  ogType = 'website',
}: PageMetaProps) {
  const fullTitle = title.includes(SEO_CONFIG.siteName) ? title : `${title} | ${SEO_CONFIG.siteName}`;
  const safeDescription = truncateForMeta(description);
  const canonicalUrl = canonical ? absoluteUrl(canonical) : undefined;
  const ogImageUrl = ogImage ?? undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={safeDescription} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
    </Helmet>
  );
}
